import { reactive, ref, watch, type Ref } from 'vue'
import type { Toast, ToastConfig } from '../types'
import { useInterpolation } from '@/utils/animation/useInterpolation'
import {
  UIDGenerator,
  VEC3,
  VEC4,
} from 'math'
import { VEC2 } from '@/utils/math/vector'
import { sleep } from '@/utils/time'
import * as constants from '../constants'
import { getToastDuration } from './utils'
import { updateToast } from './update'

const MASS = 6.1;

export type UseToastsState = {
  toasts: Ref<Toast>[]
}

export type UseToasts = {
  state: UseToastsState
  push: (toast: ToastConfig) => Promise<Ref<Toast>>
  pushToast: (toast: ToastConfig) => Promise<Ref<Toast>>
  pop: (uid?: string, force?: boolean) => Promise<any>
  popToast: (uid?: string, force?: boolean) => Promise<any>
}


const state = reactive<UseToastsState>({
  toasts: []
})

const uidGen = UIDGenerator({
  uidLength: 24
})

let interval: ReturnType<typeof requestAnimationFrame> | null = null

const getHighestToast = (): Ref<Toast> | null => {
  if (state.toasts.length <= 0) return null;
  let maxY: number = -10000;

  let maxToast: Ref<Toast> = state.toasts[0];

  for (let i = 0; i < state.toasts.length; i++) {
    const toast = state.toasts[i];
    const pos = toast.value.position;

    if (pos.y > maxY) {
      maxY = pos.y;
      maxToast = toast;
    }
  };

  return maxToast;
}

watch(
  () => state.toasts.length,
  (_len) => {
    if (state.toasts.length > 0 && interval === null) {
      let lastTime = performance.now()
      const loop = (time: number) => {
        const delta = (time - lastTime) / 1000
        lastTime = time
        state.toasts.forEach((toast) => updateToast(state, toast, delta))
        if (state.toasts.length <= 0) return
        interval = requestAnimationFrame(loop)
      }
      interval = requestAnimationFrame(loop)
    } else if (interval !== null && state.toasts.length <= 0) {
      cancelAnimationFrame(interval)
      interval = null
    }
  },
  { immediate: true, deep: true }
)

export const useToasts = (): UseToasts => {
  const push = (cfg: ToastConfig) => {
    if (state.toasts.length >= constants.TOAST_LIMIT) {
      const msg = `Too many toasts! The limit is ${constants.TOAST_LIMIT}`
      console.error(msg)
      throw new Error(msg)
    }

    const uid = uidGen.next()

    const highestToast = getHighestToast();
    const maxY = highestToast ? (highestToast.value.position.y + constants.TOAST_SPACE_BOTTOM + constants.TOAST_MIN_HEIGHT) : 0;

    const mass = MASS * (1.0 + 0.1 * state.toasts.length);
    const toast = ref<Toast>({
      ...cfg,
      trash: false,
      timeCreated: new Date(),
      mass: mass,
      invMass: 1.0 / mass,
      height: constants.TOAST_MIN_HEIGHT,
      width: constants.TOAST_MIN_WIDTH,
      position: VEC2(0, maxY),//VEC2(0, constants.TOAST_MIN_HEIGHT + 16),
      velocity: VEC2(0, 1),
      angularVelocity: VEC3(0, 0, 0),
      rotation: VEC4(0, 0, 0, 1),
      uid,
      animation: useInterpolation({
        initial: 0,
        duration: 0.5
      }),
      update: (toast) => {
        if (!toast || !toast.value) return
      }
    })

    ;(state as any).toasts = [...(state.toasts as any[]), toast]

    return new Promise<Ref<Toast>>((resolve) => {
      queueMicrotask(async () => {
        await toast.value.animation.runTo(1.0)
        await sleep(getToastDuration(toast.value as any) * 1000)
        await pop(toast.value.uid)
        resolve(toast as any)
      })
    })
  }

  const pop = (uid?: string, force?: boolean) => {
    const toast = uid
      ? state.toasts.find((toast) => toast.value.uid === uid)
      : state.toasts[state.toasts.length - 1]
    return new Promise((resolve) => {
      queueMicrotask(async () => {
        if (!toast) {
          resolve(null)
          return
        }

        toast.value.trash = true
        if (!force) {
          await toast.value.animation.runTo(0.0)
        }
        state.toasts = state.toasts.filter((t) => t.value.uid !== toast.value.uid)
      })
    })
  }

  return {
    state,
    push,
    pop,
    pushToast: push,
    popToast: pop
  }
}

export const pushToast = async (cfg: ToastConfig) => {
  const handler = useToasts()
  return await handler.push(cfg)
}

export const pushDebugMessage = async (msg: string) => {
  return await pushToast({
    title: 'Debug',
    subtitle: msg
  })
}

export const popToast = async (uid?: string, force?: boolean) => {
  const handler = useToasts()
  return await handler.pop(uid, force)
}
